import {
  EventStatus,
  EventType,
  MatrixEventEvent,
  MsgType,
  Preset,
  ReceiptType,
  RelationType,
  RoomEvent,
  RoomMemberEvent,
  UserEvent,
  type MatrixClient,
  type MatrixEvent,
  type Room,
} from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { mediaLabel, mediaOf, type MessageMedia } from './media.js';
import { isDelivered, subscribeDelivery } from './delivery.js';
import { getPrivacy } from './privacy.js';
import { DEFAULT_CHARACTER } from '../characters.js';
import { announcePreview } from '../pushPreview.js';
import {
  proxyVerdict,
  readProxyTag,
  subscribeReplySignatures,
  type ProxyTag,
} from '../replySignature.js';

export type Conversation = {
  roomId: string;
  name: string;
  isDirect: boolean;
  otherUserId: string | null;
  memberCount: number;
  /** Foto do grupo (mxc), quando alguém escolheu uma. */
  avatar: string | null;
  lastText: string;
  lastAt: number;
  unread: number;
  encrypted: boolean;
};

export type Reaction = { key: string; count: number; mine: boolean; myEventId: string | null };

export type Message = {
  id: string;
  eventId: string | null;
  senderId: string;
  senderName: string;
  text: string;
  at: number;
  mine: boolean;
  /** enviando (eco local) → enviada → lida pelos outros participantes */
  status: 'sending' | 'failed' | 'sent' | 'delivered' | 'read';
  /** posição no grupo de balões seguidos do mesmo remetente (até 3 min) */
  group: 'single' | 'first' | 'middle' | 'last';
  edited: boolean;
  redacted: boolean;
  replyTo: { senderName: string; text: string } | null;
  reactions: Reaction[];
  /** Foto, vídeo, áudio, arquivo ou figurinha; `text` vira a legenda curta. */
  media: MessageMedia | null;
  /** Veio de outra conversa (encaminhada). */
  forwarded: boolean;
  /** Fala do Secretário de alguém da conversa (participante visível). */
  assistant: {
    name: string;
    characterKey: string;
    ownerName: string;
    request: string;
    /** Dono do Secretário (id no mensageiro). */
    owner: string;
    /** Publicada por quem escreveu, em nome do Secretário do dono (assinada pelo servidor). */
    proxied: boolean;
    /** Aviso programado ou lembrete de horário de uma conta profissional. */
    kind: 'aviso' | 'lembrete' | null;
  } | null;
  /** Aviso da conversa (o Secretário entrou ou saiu), no lugar de um balão. */
  notice: string | null;
};

const DM_ALGORITHM = 'm.megolm.v1.aes-sha2';
const GROUP_WINDOW_MS = 3 * 60 * 1000;
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

export function localpart(userId: string): string {
  return userId.replace(/^@/, '').split(':')[0] ?? userId;
}

function directMap(client: MatrixClient): Record<string, string[]> {
  const content = client.getAccountData(EventType.Direct)?.getContent() as
    Record<string, string[]> | undefined;
  return content ?? {};
}

function otherUserOf(client: MatrixClient, room: Room): string | null {
  const me = client.getUserId();
  const members = room.getJoinedMembers().concat(room.getMembersWithMembership('invite'));
  const other = members.find((member) => member.userId !== me);
  return other?.userId ?? null;
}

export function isDirectRoom(client: MatrixClient, room: Room): boolean {
  const map = directMap(client);
  if (Object.values(map).some((rooms) => rooms.includes(room.roomId))) return true;
  // Sala sem nome próprio com duas pessoas é conversa direta (convite recebido antes do
  // m.direct). `room.name` não serve: sem nome, o SDK devolve o nome da outra pessoa.
  const named = room.currentState.getStateEvents(EventType.RoomName, '')?.getContent().name;
  return !named && room.getJoinedMemberCount() + room.getInvitedMemberCount() <= 2;
}

// Numa sala protegida, o evento apagado perde o conteúdo decifrado e volta a ser
// "m.room.encrypted": só sabemos que era mensagem (e não reação) se já a vimos.
const knownMessageIds = new Set<string>();

function bodyOf(event: MatrixEvent): string | null {
  if (event.isRedacted()) return 'Mensagem apagada';
  if (event.getType() !== EventType.RoomMessage && event.getType() !== EventType.Sticker)
    return null;
  if (event.isDecryptionFailure()) return 'Mensagem protegida: aguardando as chaves.';
  const media = mediaOf(event);
  if (media) return mediaLabel(media);
  const content = event.getContent();
  if (typeof content.body !== 'string') return null;
  // Edições chegam como "* texto"; o conteúdo novo vem em m.new_content.
  const fresh = content['m.new_content'] as { body?: string } | undefined;
  return fresh?.body ?? content.body;
}

function isTimelineMessage(event: MatrixEvent): boolean {
  if (event.isRedacted()) {
    const type = event.getType();
    return (
      type === EventType.RoomMessage ||
      (type === EventType.RoomMessageEncrypted && knownMessageIds.has(event.getId() ?? ''))
    );
  }
  if (event.getType() === EventType.Sticker) return true;
  if (event.getType() !== EventType.RoomMessage) return false;
  const relation = event.getRelation();
  return relation?.rel_type !== RelationType.Replace; // a edição vive no original
}

function lastMessage(room: Room): { text: string; at: number } {
  const events = room.getLiveTimeline().getEvents();
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const event = events[i]!;
    if (!isTimelineMessage(event)) {
      if (event.getType() === EventType.RoomMessageEncrypted)
        return { text: 'Mensagem protegida', at: event.getTs() };
      continue;
    }
    const text = bodyOf(event);
    if (text !== null) return { text, at: event.getTs() };
  }
  return { text: '', at: room.getLastActiveTimestamp() };
}

export function listConversations(client: MatrixClient): Conversation[] {
  return client
    .getVisibleRooms()
    .filter((room) => room.getMyMembership() === 'join')
    .map((room) => {
      const isDirect = isDirectRoom(client, room);
      const otherUserId = isDirect ? otherUserOf(client, room) : null;
      const last = lastMessage(room);
      const name =
        isDirect && otherUserId ? displayNameFor(client, room, otherUserId) : room.name || 'Grupo';
      return {
        roomId: room.roomId,
        name,
        isDirect,
        otherUserId,
        memberCount: room.getJoinedMemberCount(),
        avatar: isDirect
          ? ((otherUserId ? room.getMember(otherUserId)?.getMxcAvatarUrl() : null) ?? null)
          : room.getMxcAvatarUrl(),
        lastText: last.text,
        lastAt: last.at,
        unread: room.getUnreadNotificationCount(),
        encrypted: client.isRoomEncrypted(room.roomId),
      };
    })
    .sort((a, b) => b.lastAt - a.lastAt);
}

export function displayNameFor(client: MatrixClient, room: Room | null, userId: string): string {
  const member = room?.getMember(userId);
  if (member?.name && member.name !== userId) return member.name;
  const user = client.getUser(userId);
  return user?.displayName || localpart(userId);
}

export async function startDirectMessage(client: MatrixClient, userId: string): Promise<string> {
  const existing = (directMap(client)[userId] ?? []).find(
    (roomId) => client.getRoom(roomId)?.getMyMembership() === 'join',
  );
  if (existing) return existing;
  const { room_id: roomId } = await client.createRoom({
    is_direct: true,
    invite: [userId],
    preset: Preset.TrustedPrivateChat,
    initial_state: [
      { type: EventType.RoomEncryption, state_key: '', content: { algorithm: DM_ALGORITHM } },
    ],
  });
  const map = {
    ...directMap(client),
    [userId]: [...(directMap(client)[userId] ?? []), roomId],
  };
  await client.setAccountData(EventType.Direct, map);
  return roomId;
}

function readByOthers(room: Room, me: string | null, eventId: string | null): boolean {
  if (!eventId) return false;
  return room
    .getJoinedMembers()
    .filter((member) => member.userId !== me)
    .some((member) => room.hasUserReadEvent(member.userId, eventId));
}

function reactionsOf(room: Room, me: string | null, eventId: string | null): Reaction[] {
  if (!eventId) return [];
  const relations = room
    .getUnfilteredTimelineSet()
    .relations.getChildEventsForEvent(eventId, RelationType.Annotation, EventType.Reaction);
  const sorted = relations?.getSortedAnnotationsByKey() ?? [];
  return sorted
    .map(([key, events]) => {
      const live = [...events].filter((event) => !event.isRedacted());
      const mineEvent = live.find((event) => event.getSender() === me);
      return { key, count: live.length, mine: !!mineEvent, myEventId: mineEvent?.getId() ?? null };
    })
    .filter((reaction) => reaction.count > 0);
}

export function timelineMessages(client: MatrixClient, room: Room): Message[] {
  const me = client.getUserId();
  // Ordem cronológica: os eventos pendentes já aparecem na própria timeline.
  const messages = room
    .getLiveTimeline()
    .getEvents()
    .flatMap((event) => {
      const notice = assistantNotice(event);
      if (notice) return [notice];
      if (!isTimelineMessage(event)) return [];
      const text = bodyOf(event);
      if (text === null) return [];
      const eventId = event.getId() ?? null;
      if (eventId && hiddenIds().has(eventId)) return [];
      if (eventId && !event.isRedacted()) knownMessageIds.add(eventId);
      const pending = event.status !== null;
      const assistant = event.isRedacted() ? null : assistantTag(event, room, me);
      // Fala publicada por procuração não é de quem a publicou: é do Secretário do outro.
      const proxied = assistant?.proxied === true;
      const replyId = event.replyEventId;
      const replied = replyId ? room.findEventById(replyId) : undefined;
      return [
        {
          // O eco local mantém o txnId depois de enviado: chave estável, sem remontar o balão.
          id: event.getTxnId() ?? eventId ?? String(event.getTs()),
          eventId,
          senderId: proxied ? `secretario:${assistant.owner}` : (event.getSender() ?? ''),
          senderName: displayNameFor(client, room, event.getSender() ?? ''),
          text,
          at: event.getTs(),
          mine: event.getSender() === me && !proxied,
          status: pending
            ? event.status === EventStatus.NOT_SENT
              ? ('failed' as const)
              : ('sending' as const)
            : readByOthers(room, me, eventId)
              ? ('read' as const)
              : isDelivered(eventId)
                ? ('delivered' as const)
                : ('sent' as const),
          group: 'single' as Message['group'],
          edited: event.replacingEvent() !== null,
          redacted: event.isRedacted(),
          replyTo: replied
            ? {
                senderName: displayNameFor(client, room, replied.getSender() ?? ''),
                text: bodyOf(replied) ?? 'Mensagem',
              }
            : null,
          reactions: reactionsOf(room, me, eventId),
          media: event.isRedacted() || event.isDecryptionFailure() ? null : mediaOf(event),
          forwarded: !event.isRedacted() && event.getContent()[FORWARDED_FIELD] === true,
          assistant,
          notice: null,
        },
      ];
    });
  for (let i = 0; i < messages.length; i += 1) {
    const current = messages[i]!;
    const previous = messages[i - 1];
    const next = messages[i + 1];
    const joinsPrevious =
      !!previous &&
      previous.senderId === current.senderId &&
      current.at - previous.at < GROUP_WINDOW_MS;
    const joinsNext =
      !!next && next.senderId === current.senderId && next.at - current.at < GROUP_WINDOW_MS;
    current.group =
      joinsPrevious && joinsNext
        ? 'middle'
        : joinsPrevious
          ? 'last'
          : joinsNext
            ? 'first'
            : 'single';
  }
  return messages;
}

export async function sendText(
  client: MatrixClient,
  roomId: string,
  text: string,
  replyToEventId?: string,
): Promise<void> {
  await announcePreview(client, roomId, text);
  if (replyToEventId) {
    await client.sendMessage(roomId, {
      msgtype: MsgType.Text,
      body: text,
      'm.relates_to': { 'm.in_reply_to': { event_id: replyToEventId } },
    });
    return;
  }
  await client.sendTextMessage(roomId, text);
}

export async function editText(
  client: MatrixClient,
  roomId: string,
  eventId: string,
  text: string,
): Promise<void> {
  await client.sendMessage(roomId, {
    msgtype: MsgType.Text,
    body: `* ${text}`,
    'm.new_content': { msgtype: MsgType.Text, body: text },
    'm.relates_to': { rel_type: RelationType.Replace, event_id: eventId },
  });
}

export async function toggleReaction(
  client: MatrixClient,
  roomId: string,
  message: Message,
  key: string,
): Promise<void> {
  if (!message.eventId) return;
  const existing = message.reactions.find((reaction) => reaction.key === key && reaction.mine);
  if (existing?.myEventId) {
    await client.redactEvent(roomId, existing.myEventId);
    return;
  }
  await client.sendEvent(roomId, EventType.Reaction, {
    'm.relates_to': { rel_type: RelationType.Annotation, event_id: message.eventId, key },
  });
}

// ── o Secretário como participante visível da conversa ─────────────────────
const ASSISTANT_STATE = 'net.secretarios.assistant';
const ASSISTANT_FIELD = 'net.secretarios.assistant';

type AssistantIdentity = { name: string; characterKey: string; ownerName: string };

/**
 * Como o Secretário age numa conversa em que foi incluído:
 * - pedido: só fala quando o dono pede;
 * - chamado: também atende quem o chama pelo nome;
 * - sozinho: além disso, responde quando o dono não está na conversa.
 */
export type AssistantMode = 'pedido' | 'chamado' | 'sozinho';

function modeOf(content: Record<string, unknown>): AssistantMode {
  return content.mode === 'chamado' || content.mode === 'sozinho' ? content.mode : 'pedido';
}

function text80(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim().slice(0, 80) : fallback;
}

/**
 * A marca vale em dois casos: quem enviou é o dono daquele Secretário, ou a
 * fala foi publicada por procuração e a assinatura do servidor confere (mesma
 * conversa, mesma mensagem respondida, mesmo texto).
 */
function assistantTag(event: MatrixEvent, room: Room, me: string | null): Message['assistant'] {
  const tag = event.getContent()[ASSISTANT_FIELD] as Record<string, unknown> | undefined;
  if (!tag || typeof tag !== 'object') return null;
  const sender = event.getSender();
  const shown = (owner: string, proxied: boolean) => ({
    name: text80(tag.name, 'Secretário'),
    characterKey: text80(tag.characterKey, DEFAULT_CHARACTER),
    ownerName: text80(tag.ownerName, ''),
    request: typeof tag.request === 'string' ? tag.request.slice(0, 500) : '',
    owner,
    proxied,
    kind:
      typeof tag.replyTo === 'string' && tag.replyTo.startsWith('notice:')
        ? ('aviso' as const)
        : typeof tag.replyTo === 'string' && tag.replyTo.startsWith('reminder:')
          ? ('lembrete' as const)
          : null,
  });
  if (sender && tag.owner === sender) return shown(sender, false);

  const proxy = readProxyTag(tag);
  // Editada, perde o selo: a assinatura vale para o texto original.
  if (!proxy || !sender || event.replacingEvent()) return null;
  if (room.getMember(proxy.owner)?.membership !== 'join') return null;
  const eventId = event.getId();
  // Enquanto a minha publicação ainda está saindo, vale o que o servidor acabou de me entregar;
  // depois de enviada, ela é conferida como qualquer outra.
  if (sender === me && (!eventId || !eventId.startsWith('$'))) return shown(proxy.owner, true);
  if (!eventId) return null;
  const original = room.findEventById(proxy.replyTo);
  const readable =
    !!original &&
    original.getSender() === sender &&
    original.getType() === EventType.RoomMessage &&
    !original.isRedacted() &&
    !original.isDecryptionFailure();
  const verdict = proxyVerdict({
    eventId,
    roomId: room.roomId,
    sender,
    body: String(event.getContent().body ?? ''),
    at: event.getTs(),
    incoming: readable ? String(original.getOriginalContent().body ?? '') : null,
    tag: proxy,
  });
  // Enquanto confere (instantes), já aparece como fala do Secretário; se não conferir, volta a ser mensagem comum.
  return verdict === 'invalid' ? null : shown(proxy.owner, true);
}

function assistantNotice(event: MatrixEvent): Message | null {
  if (event.getType() !== ASSISTANT_STATE || event.getStateKey() !== event.getSender()) return null;
  const content = event.getContent() as Record<string, unknown>;
  const name = text80(content.name, 'O Secretário');
  const owner = text80(content.ownerName, '');
  const who = owner ? `${name}, Secretário de ${owner},` : name;
  const id = event.getId() ?? String(event.getTs());
  return {
    id,
    eventId: null,
    senderId: `aviso:${id}`,
    senderName: '',
    text: '',
    at: event.getTs(),
    mine: false,
    status: 'sent',
    group: 'single',
    edited: false,
    redacted: false,
    replyTo: null,
    reactions: [],
    media: null,
    forwarded: false,
    assistant: null,
    notice: assistantNoticeText(event, content, name, owner, who),
  };
}

function assistantNoticeText(
  event: MatrixEvent,
  content: Record<string, unknown>,
  name: string,
  owner: string,
  who: string,
): string {
  if (content.enabled !== true) return `${who} saiu da conversa`;
  const mode = modeOf(content);
  const boss = owner || 'quem incluiu';
  const previous = event.getPrevContent() as Record<string, unknown>;
  if (previous.enabled === true) {
    if (mode === 'chamado') return `${name} agora atende quem chamar pelo nome`;
    if (mode === 'sozinho') return `${name} agora responde quando ${boss} não está`;
    return `${name} agora só fala quando ${boss} pede`;
  }
  if (mode === 'chamado') return `${who} entrou na conversa. Para falar, é só chamar: "${name}, …"`;
  if (mode === 'sozinho') return `${who} entrou na conversa e responde quando ${boss} não está`;
  return `${who} entrou na conversa`;
}

/** Como o meu Secretário está agindo nesta conversa (nada quando não está incluído). */
export function myAssistantMode(client: MatrixClient, room: Room): AssistantMode | null {
  const me = client.getUserId();
  if (!me) return null;
  const content = room.currentState.getStateEvents(ASSISTANT_STATE, me)?.getContent();
  return content?.enabled === true ? modeOf(content as Record<string, unknown>) : null;
}

/**
 * O que o dono definiu para o Secretário nesta conversa: nada ainda ('unset'),
 * tirou da conversa ('off') ou um dos modos.
 */
export function myAssistantSetting(
  client: MatrixClient,
  room: Room,
): 'unset' | 'off' | AssistantMode {
  const me = client.getUserId();
  const event = me ? room.currentState.getStateEvents(ASSISTANT_STATE, me) : null;
  if (!event) return 'unset';
  const content = event.getContent() as Record<string, unknown>;
  return content.enabled === true ? modeOf(content) : 'off';
}

/** O meu Secretário está incluído nesta conversa? */
export function myAssistantIncluded(client: MatrixClient, room: Room): boolean {
  const me = client.getUserId();
  if (!me) return false;
  return room.currentState.getStateEvents(ASSISTANT_STATE, me)?.getContent().enabled === true;
}

/** Inclui ou tira o meu Secretário (aviso visível para todos na conversa). */
export async function setAssistantIncluded(
  client: MatrixClient,
  roomId: string,
  enabled: boolean,
  identity: AssistantIdentity,
  mode: AssistantMode = 'pedido',
): Promise<void> {
  const me = client.getUserId();
  if (!me) throw new Error('Sessão do mensageiro indisponível.');
  await client.sendStateEvent(
    roomId,
    ASSISTANT_STATE as never,
    { enabled, mode, ...identity } as never,
    me,
  );
}

/** Trecho recente da conversa (só texto), lido neste aparelho para acompanhar um pedido. */
export function recentTranscript(
  client: MatrixClient,
  room: Room,
  limit = 30,
): { from: string; mine: boolean; text: string }[] {
  return timelineMessages(client, room)
    .filter((message) => !message.notice && !message.redacted && message.text.trim())
    .slice(-limit)
    .map((message) => ({
      from: (message.assistant ? message.assistant.name : message.senderName).slice(0, 80) || '—',
      mine: message.mine && !message.assistant,
      text: (message.media ? `[${message.text}]` : message.text).slice(0, 800),
    }));
}

/**
 * Publica a fala do Secretário na conversa, marcada com o nome e o personagem
 * dele. Com `proxy`, a fala é do Secretário de outra pessoa e vai com a
 * assinatura do servidor; sem ele, é o meu Secretário.
 */
export async function postAssistantMessage(
  client: MatrixClient,
  roomId: string,
  message: AssistantIdentity & {
    text: string;
    request: string;
    /** Mensagem que está sendo respondida, quando é resposta automática. */
    replyTo?: string | undefined;
    proxy?: ProxyTag | undefined;
  },
): Promise<void> {
  await announcePreview(client, roomId, `${message.name}: ${message.text}`);
  await client.sendMessage(roomId, {
    msgtype: MsgType.Text,
    body: message.text,
    [ASSISTANT_FIELD]: message.proxy
      ? { ...message.proxy, request: '' }
      : {
          owner: client.getUserId(),
          name: message.name,
          characterKey: message.characterKey,
          ownerName: message.ownerName,
          request: message.request,
          ...(message.replyTo ? { replyTo: message.replyTo } : {}),
        },
  } as never);
}

/** Já existe na conversa uma fala do Secretário respondendo a esta mensagem? */
export function hasAssistantReplyTo(room: Room, eventId: string): boolean {
  return room
    .getLiveTimeline()
    .getEvents()
    .some((event) => {
      const tag = event.getContent()[ASSISTANT_FIELD] as Record<string, unknown> | undefined;
      return !event.isRedacted() && tag?.replyTo === eventId;
    });
}

/**
 * Trecho para a resposta por procuração: só as minhas falas e as do
 * Secretário de quem está do outro lado. Fala do dono não vai.
 */
export function proxyTranscript(
  client: MatrixClient,
  room: Room,
  ownerUserId: string,
  limit = 30,
): { who: 'me' | 'secretary'; text: string }[] {
  const lines: { who: 'me' | 'secretary'; text: string }[] = [];
  for (const message of timelineMessages(client, room)) {
    if (message.notice || message.redacted) continue;
    const text = (message.media ? `[${message.text}]` : message.text).trim().slice(0, 800);
    if (!text) continue;
    if (message.assistant) {
      if (message.assistant.owner === ownerUserId) lines.push({ who: 'secretary', text });
    } else if (message.mine) {
      lines.push({ who: 'me', text });
    }
  }
  return lines.slice(-limit);
}

/** Quem está do outro lado de uma conversa direta. */
export function directCounterpart(client: MatrixClient, room: Room): string | null {
  return isDirectRoom(client, room) ? otherUserOf(client, room) : null;
}

// ── apagar só para mim: a lista fica neste aparelho ────────────────────────
const HIDDEN_KEY = 'secretario.ocultas.v1';
const HIDDEN_MAX = 2000;
let hiddenCache: Set<string> | undefined;

function hiddenIds(): Set<string> {
  if (!hiddenCache) {
    try {
      const stored = JSON.parse(window.localStorage.getItem(HIDDEN_KEY) ?? '[]') as unknown;
      hiddenCache = new Set(Array.isArray(stored) ? stored.map(String) : []);
    } catch {
      hiddenCache = new Set();
    }
  }
  return hiddenCache;
}

/** Some com a mensagem só neste aparelho; para os outros nada muda. */
export function hideForMe(eventId: string): void {
  const ids = hiddenIds();
  ids.add(eventId);
  try {
    window.localStorage.setItem(HIDDEN_KEY, JSON.stringify([...ids].slice(-HIDDEN_MAX)));
  } catch {
    /* sem armazenamento: vale até fechar o app */
  }
}

/** Prazo para apagar para todos: 60 horas depois do envio. */
export const DELETE_FOR_ALL_WINDOW_MS = 60 * 60 * 60 * 1000;

export function canDeleteForAll(message: Message): boolean {
  return message.mine && !!message.eventId && Date.now() - message.at <= DELETE_FOR_ALL_WINDOW_MS;
}

// ── encaminhar ─────────────────────────────────────────────────────────────
const FORWARDED_FIELD = 'net.secretarios.forwarded';

/**
 * Encaminha uma mensagem (texto, mídia ou figurinha) para outra conversa. O
 * anexo não sobe de novo: segue a mesma referência cifrada, dentro de um
 * evento cifrado para a conversa de destino.
 */
export async function forwardMessage(
  client: MatrixClient,
  room: Room,
  eventId: string,
  targetRoomId: string,
): Promise<void> {
  const event = room.findEventById(eventId);
  if (!event || event.isRedacted() || event.isDecryptionFailure()) {
    throw new Error('Mensagem indisponível.');
  }
  const content: Record<string, unknown> = { ...event.getContent() };
  delete content['m.relates_to'];
  delete content['m.new_content'];
  // Encaminhada, a fala deixa de ser do Secretário: a marca vale só na conversa de origem.
  delete content[ASSISTANT_FIELD];
  content[FORWARDED_FIELD] = true;
  await client.sendEvent(targetRoomId, event.getType() as never, content as never);
}

export async function deleteMessage(
  client: MatrixClient,
  roomId: string,
  eventId: string,
): Promise<void> {
  await client.redactEvent(roomId, eventId);
}

export function typingNames(client: MatrixClient, room: Room): string[] {
  const me = client.getUserId();
  return room
    .getJoinedMembers()
    .filter((member) => member.userId !== me && member.typing)
    .map((member) => displayNameFor(client, room, member.userId));
}

export function setTyping(client: MatrixClient, roomId: string, typing: boolean): void {
  if (typing && !getPrivacy().typing) return; // quem desligou o "digitando" não avisa
  void client.sendTyping(roomId, typing, 6000).catch(() => {});
}

export async function markRead(client: MatrixClient, room: Room): Promise<void> {
  const events = room.getLiveTimeline().getEvents();
  const last = events[events.length - 1];
  // Sem confirmação de leitura: o recibo fica privado (some o contador, ninguém vê os ✓✓).
  const type = getPrivacy().readReceipts ? ReceiptType.Read : ReceiptType.ReadPrivate;
  if (last) await client.sendReadReceipt(last, type).catch(() => {});
}

/** Reage a qualquer mudança nas salas (timeline, decriptação, membros, recibos, digitação). */
export function useRoomsTick(client: MatrixClient | null, roomId?: string): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!client) return;
    const bump = () => setTick((t) => t + 1);
    const onTimeline = (_event: MatrixEvent, room?: Room) => {
      if (!roomId || room?.roomId === roomId) bump();
    };
    client.on(RoomEvent.Timeline, onTimeline);
    client.on(RoomEvent.LocalEchoUpdated, bump);
    client.on(RoomEvent.Receipt, bump);
    client.on(RoomEvent.MyMembership, bump);
    client.on(RoomEvent.Name, bump);
    client.on(RoomEvent.Redaction, bump);
    client.on(RoomMemberEvent.Typing, bump);
    client.on(UserEvent.Presence, bump);
    client.on(MatrixEventEvent.Decrypted, bump);
    const stopDelivery = subscribeDelivery(bump);
    const stopSignatures = subscribeReplySignatures(bump);
    return () => {
      stopDelivery();
      stopSignatures();
      client.off(RoomEvent.Timeline, onTimeline);
      client.off(RoomEvent.LocalEchoUpdated, bump);
      client.off(RoomEvent.Receipt, bump);
      client.off(RoomEvent.MyMembership, bump);
      client.off(RoomEvent.Name, bump);
      client.off(RoomEvent.Redaction, bump);
      client.off(RoomMemberEvent.Typing, bump);
      client.off(UserEvent.Presence, bump);
      client.off(MatrixEventEvent.Decrypted, bump);
    };
  }, [client, roomId]);
  return tick;
}

// ── Grupos e pessoas (T2.3.3 / T2.4.3) ────────────────────────────────────

export type Member = { userId: string; name: string; isMe: boolean; invited: boolean };

export async function createGroup(
  client: MatrixClient,
  name: string,
  userIds: string[],
): Promise<string> {
  const { room_id: roomId } = await client.createRoom({
    name,
    invite: userIds,
    preset: Preset.PrivateChat,
    initial_state: [
      { type: EventType.RoomEncryption, state_key: '', content: { algorithm: DM_ALGORITHM } },
    ],
  });
  return roomId;
}

export function roomMembers(client: MatrixClient, room: Room): Member[] {
  const me = client.getUserId();
  const joined = room.getJoinedMembers().map((member) => ({
    userId: member.userId,
    name: displayNameFor(client, room, member.userId),
    isMe: member.userId === me,
    invited: false,
  }));
  const invited = room.getMembersWithMembership('invite').map((member) => ({
    userId: member.userId,
    name: displayNameFor(client, room, member.userId),
    isMe: false,
    invited: true,
  }));
  return [...joined.filter((m) => m.isMe), ...joined.filter((m) => !m.isMe), ...invited];
}

export async function inviteToRoom(
  client: MatrixClient,
  roomId: string,
  userId: string,
): Promise<void> {
  await client.invite(roomId, userId);
}

export async function renameRoom(
  client: MatrixClient,
  roomId: string,
  name: string,
): Promise<void> {
  await client.setRoomName(roomId, name);
}

/** Sair apaga a conversa deste lado: a sala some da lista e não volta. */
export async function leaveRoom(client: MatrixClient, roomId: string): Promise<void> {
  await client.leave(roomId);
  await client.forget(roomId).catch(() => {});
  const map = directMap(client);
  const cleaned = Object.fromEntries(
    Object.entries(map)
      .map(([user, rooms]) => [user, rooms.filter((id) => id !== roomId)] as const)
      .filter(([, rooms]) => rooms.length > 0),
  );
  if (JSON.stringify(cleaned) !== JSON.stringify(map)) {
    await client.setAccountData(EventType.Direct, cleaned).catch(() => {});
  }
}

export function isBlocked(client: MatrixClient, userId: string): boolean {
  return client.isUserIgnored(userId);
}

/** Bloquear = ignorar no servidor: as mensagens da pessoa deixam de chegar neste usuário. */
export async function setBlocked(
  client: MatrixClient,
  userId: string,
  blocked: boolean,
): Promise<void> {
  const current = client.getIgnoredUsers();
  const next = blocked
    ? Array.from(new Set([...current, userId]))
    : current.filter((id) => id !== userId);
  await client.setIgnoredUsers(next);
}

// ── Envio que falhou e presença (T2.5.1 / T2.5.3) ─────────────────────────

function pendingEvent(room: Room, message: Message): MatrixEvent | undefined {
  return room
    .getLiveTimeline()
    .getEvents()
    .find((event) => (event.getTxnId() ?? event.getId()) === message.id);
}

/** Tenta de novo uma mensagem que não saiu (sem conexão, por exemplo). */
export async function retrySend(client: MatrixClient, room: Room, message: Message): Promise<void> {
  const event = pendingEvent(room, message);
  if (event) await client.resendEvent(event, room).catch(() => {});
}

export function discardSend(client: MatrixClient, room: Room, message: Message): void {
  const event = pendingEvent(room, message);
  if (event) client.cancelPendingEvent(event);
}

/** "online" só aparece para quem escolheu mostrar (o servidor só recebe de quem liga). */
export function isOnline(client: MatrixClient, userId: string | null): boolean {
  if (!userId) return false;
  const user = client.getUser(userId);
  return user?.presence === 'online' && user.currentlyActive !== false;
}
